'use client';
import React from 'react';
import { I, card, ConfirmButton, useConfirmation } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';
import { styleBouton } from '@/shared/boutonStyles';
import type { RhIntegrationPiece } from '@/lib/types';

/**
 * LES PIÈCES DU DOSSIER D'UNE PERSONNE — une seule définition, deux appelants.
 *
 * ── Pourquoi ce fichier existe (RQ, 06/10/2026) ────────────────────────────
 *
 * « Je voudrais que le document soit ici pour modifier ou rajouter des
 * documents si besoin (bien que l'intégration soit terminée). »
 *
 * Le bloc vivait dans `RhIntegration.tsx` et n'était atteignable que depuis le
 * parcours d'intégration. Le dossier RH étant devenu le hub de la personne, il
 * lui faut les pièces — en écriture, pas en vitrine. La façon rapide aurait été
 * de recopier le composant : c'est exactement le schéma des deux « jumeaux »
 * des 12 et 13/09/2026 — deux copies, un avertissement ajouté d'un seul côté, et
 * la seconde fenêtre qui finit par ne plus dire que la consultation est tracée.
 * D'où l'extraction, sur le modèle de `RattacherCompte`.
 *
 * ── ⚠️ UN DOSSIER CLÔTURÉ N'EST PLUS FIGÉ POUR SES PIÈCES ─────────────────
 *
 * Le bloc refusait dépôt et retrait dès que le parcours était clos (`clos`).
 * Trois raisons de lever ce verrou, et pas seulement la demande :
 *
 *  · le moteur ne l'a JAMAIS exigé — `POST /ged/rh/personnel/:orgId/pieces` ne
 *    regarde pas `statut` ; le verrou n'était donc qu'à l'écran ;
 *  · la rubrique s'intitule, dans le parcours lui-même, « AU FIL DE L'EAU » :
 *    des pièces qui arrivent avec le temps, par définition ;
 *  · une pièce administrative s'ajoute toute la vie — un diplôme obtenu en
 *    2027, un RIB qui change de banque, une CIN renouvelée. Les refuser après
 *    la clôture obligeait à rouvrir un parcours terminé pour déposer un
 *    papier : un dossier d'intégration « en cours » qui ne l'est pas, c'est-à-
 *    dire une affirmation fausse au seul motif d'un classement.
 *
 * `clos` subsiste et ne sert plus qu'à le DIRE — « le parcours est clôturé, les
 * pièces continuent de s'y classer » —, jamais à bloquer. Les deux appelants
 * ont donc le même pouvoir : un seul composant, une seule règle, aucune
 * divergence à tenir.
 *
 * ── Ce qui ne change pas ───────────────────────────────────────────────────
 *
 * ⚠️ LE CONTENU NE SORT QUE PIÈCE PAR PIÈCE, et chaque passage laisse une trace
 * nominative au journal (`IntegrationPieceConsultee`). La liste ne porte que des
 * métadonnées : ni `GET /ged/rh/personnel/:orgId/pieces` ni la liste des parcours
 * ne servent `fichierData`. Une pièce
 * marquée « donnée personnelle » — identité, photo, coordonnées bancaires — pose
 * en plus une confirmation qui annonce la trace AVANT de l'écrire.
 *
 * ⚠️ DÉPÔT, LECTURE ET RETRAIT EXIGENT LE RÔLE `rh` côté moteur (`exigerRh`,
 * 05/10/2026) — et non « RQ ou RH » comme le reste de la fiche. C'est à
 * l'appelant de passer `modifiable` en conséquence : proposer les boutons à un
 * RQ sans ce rôle donnerait un 403 à chaque clic, ce qui se lit comme une panne
 * et non comme un droit manquant.
 */

/** La check-list `RH LIS check list recrutement personnel`, dans son ordre. */
export const PIECES_ATTENDUES: { cle: string; libelle: string; sensible: boolean }[] = [
  { cle: 'cv', libelle: 'CV', sensible: false },
  { cle: 'lettre', libelle: 'Lettre de motivation', sensible: false },
  { cle: 'formations', libelle: 'Formations', sensible: false },
  { cle: 'diplomes', libelle: 'Diplômes', sensible: false },
  { cle: 'cin', libelle: 'Copie carte d’identité', sensible: true },
  { cle: 'photo', libelle: 'Photo', sensible: true },
  { cle: 'rib', libelle: 'Copie RIB bancaire', sensible: true },
  { cle: 'engagement_qualite', libelle: 'Engagement qualité signé', sensible: false },
  { cle: 'engagement_confidentialite', libelle: 'Engagement confidentialité signé', sensible: false },
];

export function BlocPieces({ pieces, clos = false, modifiable = true, onDeposer, onConsulter, onSupprimer }: {
  /** ⚠️ LES PIÈCES, ET NON UN DOSSIER D'INTÉGRATION (08/10/2026). Le composant
   *  recevait `dossier: RhIntegration` et n'en lisait que `.pieces` : il était
   *  donc impossible de l'employer pour quelqu'un sans parcours, c'est-à-dire
   *  pour tout le personnel entré avant 2026. Il reçoit maintenant la liste, et
   *  l'appelant la tient d'où il veut — du parcours, ou de la personne. */
  pieces: RhIntegrationPiece[];
  /** Le parcours est clôturé. INFORMATIF : il ne bloque plus rien — voir
   *  l'en-tête de ce fichier pour les trois raisons. */
  clos?: boolean;
  /** Le rôle `rh` est porté. Faux : la liste se lit, rien ne s'écrit et aucune
   *  pièce ne s'ouvre — le moteur refuserait les trois. */
  modifiable?: boolean;
  onDeposer: (cle: string, libelle: string, f: File) => void;
  onConsulter: (id: string) => Promise<{ fichierNom: string; fichierData: string } | null>;
  onSupprimer: (id: string) => void;
}) {
  const { demander, confirmation } = useConfirmation();
  const parCle = new Map<string, RhIntegrationPiece>();
  for (const p of pieces) parCle.set(p.cle, p);
  /* Les pièces déposées sous une clé HORS check-list — il n'y en a pas
     aujourd'hui (les deux écrans n'offrent que les neuf), mais une reprise de
     données ou une clé retirée de la liste en produirait. Les taire ferait
     disparaître un document du dossier sans que rien ne le dise. */
  const horsListe = pieces.filter((p) => !PIECES_ATTENDUES.some((a) => a.cle === p.cle));

  const ouvrir = async (p: RhIntegrationPiece) => {
    if (p.sensible && !(await demander(
      `« ${p.libelle} » est une donnée personnelle.\n\nVotre consultation sera enregistrée à votre nom dans le journal du laboratoire.\n\nOuvrir ?`,
      { confirmer: 'Ouvrir' },
    ))) return;
    const d = await onConsulter(p.id);
    if (!d?.fichierData) return;
    const bin = atob(d.fichierData);
    const buf = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) buf[i] = bin.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([buf]));
    const a = document.createElement('a');
    a.href = url; a.download = d.fichierNom || p.libelle;
    a.click();
    URL.revokeObjectURL(url);
  };

  /** Une ligne de la check-list, ou une pièce hors liste. */
  const ligne = (cle: string, libelle: string, sensible: boolean, p?: RhIntegrationPiece) => (
    <div key={cle} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0',
      borderTop: '1px solid var(--border-subtle)' }}>
      {/* Pièce absente : pas de pictogramme plutôt qu'une croix — elle n'est
          pas refusée, elle n'est pas encore là. Le bouton « Déposer » dit quoi faire. */}
      <span style={{ flex: 'none', width: 18, color: '#1F7A45' }}>
        {p ? <I n="check" s={13} /> : null}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: 12.5, color: p ? 'var(--text-strong)' : 'var(--text-muted)' }}>
          {libelle}
          {sensible && (
            <span style={{ marginLeft: 7, fontSize: 12.5, fontWeight: 700, color: 'var(--amber-600)',
              background: '#FCF2E1', borderRadius: 'var(--radius-sm)', padding: '1px 6px' }}>
              donnée personnelle
            </span>
          )}
        </span>
        {p && (
          <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-muted)' }}>
            {p.fichierNom} · déposée le {p.deposeLe} par {p.deposePar}
          </span>
        )}
      </div>
      {p ? (
        <>
          {modifiable && <Bouton taille="sm" onClick={() => void ouvrir(p)}>Ouvrir</Bouton>}
          {modifiable && (
            <>
              {/* REMPLACER et non « retirer puis déposer » : une CIN renouvelée,
                  un RIB qui change de banque. Le moteur crée une nouvelle ligne
                  et la liste retient la dernière par clé — l'ancienne n'est donc
                  pas perdue au journal, mais elle sort du dossier courant. */}
              <label style={{ ...styleBouton('contour', 'sm'), cursor: 'pointer' }}>
                Remplacer
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.heic,.heif" style={{ display: 'none' }}
                  title="PDF ou image, 8 Mo maximum"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) onDeposer(cle, libelle, f); e.target.value = ''; }} />
              </label>
              <ConfirmButton label="Retirer" variant="danger" question={`Retirer « ${libelle} » du dossier ?`}
                onConfirm={() => onSupprimer(p.id)} />
            </>
          )}
        </>
      ) : modifiable && (
        <label style={{ ...styleBouton('contour', 'sm'), cursor: 'pointer' }}>
          Déposer
          <input type="file" accept=".pdf,.jpg,.jpeg,.png,.heic,.heif" style={{ display: 'none' }}
            title="PDF ou image, 8 Mo maximum"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onDeposer(cle, libelle, f); e.target.value = ''; }} />
        </label>
      )}
    </div>
  );

  return (
    <div style={{ ...card, padding: 18, marginTop: 12 }}>
      <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '0.05em', color: 'var(--text-strong)' }}>
        Pièces du dossier
      </h3>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '6px 0 12px', lineHeight: 1.55 }}>
        Check-list de recrutement du laboratoire. Les pièces marquées
        <strong> donnée personnelle </strong>— identité, photo, coordonnées bancaires — ne sortent
        jamais dans une liste : elles s’ouvrent une par une, et <strong>chaque consultation est
        enregistrée à votre nom</strong> dans le journal.
      </p>
      {/* Le fait, sans la conséquence qu'il n'a plus : la clôture ne ferme pas
          le classement. Le taire laisserait croire qu'on modifie un dossier
          encore en cours. */}
      {clos && (
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '-6px 0 12px', lineHeight: 1.55 }}>
          Le parcours d’intégration est <strong>clôturé</strong> ; les pièces continuent de s’y
          classer — un diplôme obtenu plus tard, une carte d’identité renouvelée.
        </p>
      )}
      {!modifiable && (
        <p style={{ fontSize: 12.5, color: 'var(--text-faint)', margin: '-6px 0 12px', lineHeight: 1.55 }}>
          Déposer, ouvrir ou retirer une pièce relève du <strong>gestionnaire RH</strong> : ce sont
          des pièces administratives, et leur contenu ne sort que sur une trace nominative.
        </p>
      )}

      {PIECES_ATTENDUES.map((att) => ligne(att.cle, att.libelle, att.sensible, parCle.get(att.cle)))}
      {!!horsListe.length && (
        <>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '12px 0 0', lineHeight: 1.5 }}>
            Hors check-list — déposées sous une autre clé :
          </p>
          {horsListe.map((p) => ligne(p.cle, p.libelle || p.cle, p.sensible, p))}
        </>
      )}
      {confirmation}
    </div>
  );
}
